/**
 * Cosmetic, client-side normalizers for campaign mentions/hashtags.
 *
 * These ONLY tidy input shape for UX (the server, via ValidSocialCreativeTokens,
 * is the source of truth for validation). Mirrors the safe transforms of the
 * backend normalizeSafe(): collapse whitespace, dedupe signs — never strip
 * charset-invalid characters.
 *
 * Exposed both as ES exports and on a single intentional global
 * (`window.SocialTokens`) so Blade inline scripts and bundled modules share one
 * implementation with no duplication.
 */

// Whitespace + zero-width / BOM characters. JS \s already covers NBSP and most
// Unicode separators; the explicit escapes add zero-width space/joiners + BOM.
const SPLIT = new RegExp('[\\s\\u200B\\u200C\\u200D\\uFEFF]+', 'g');

/** Single handle: strip signs, turn internal whitespace into `_`, re-prefix `@`.
 *  Underscores typed by the user are preserved (incl. leading/trailing/repeated). */
export function normalizeMention(value) {
    if (!value) {
        return '';
    }
    const body = String(value)
        .replace(/[@#]/g, '')   // strip signs
        .split(SPLIT)           // split on whitespace / zero-width
        .filter(Boolean)        // drop empty parts -> trims edge whitespace only
        .join('_');             // each whitespace run -> a single underscore
    return body ? '@' + body : '';
}

/** Multiple hashtags: split on whitespace, strip signs, re-prefix each `#`. */
export function normalizeHashtag(value) {
    if (!value) {
        return '';
    }
    return String(value)
        .split(SPLIT)
        .map((token) => token.replace(/[@#]/g, ''))
        .filter(Boolean)
        .map((token) => '#' + token)
        .join(' ');
}


function toRegex(php) {
    if (typeof php !== 'string' || php.length < 2 || php[0] !== '/') {
        return null;
    }
    const last = php.lastIndexOf('/');
    if (last <= 0) {
        return null;
    }
    const body = php.slice(1, last);
    const flags = php.slice(last + 1).replace(/[^gimsuy]/g, '');
    try {
        return new RegExp(body, flags);
    } catch (e) {
        return null;
    }
}

function canonicalSlug(slug, rules) {
    const s = String(slug || '').toLowerCase().trim();
    const aliases = (rules && rules.slug_aliases) || {};
    return aliases[s] || s;
}

/** Validate a single @handle for a platform. Mirrors the mention branch (allowMultiple=false). */
export function validateMention(value, slug, rules) {
    const v = value == null ? '' : String(value).trim();
    if (v === '') {
        return { valid: true, message: '' };
    }

    const tokens = v.split(SPLIT).filter(Boolean);
    if (!tokens.length) {
        return { valid: false, message: 'Please enter a valid @handle (e.g. @username).' };
    }
    if (tokens.length > 1) {
        return { valid: false, message: 'Please enter a single @handle for this platform (no spaces).' };
    }

    const token = tokens[0];
    if (!/^@[^\s@#]+$/u.test(token)) {
        return { valid: false, message: 'A mention must start with @ and contain no spaces, # or additional @ (e.g. @username).' };
    }

    const cslug = canonicalSlug(slug, rules);
    const handles = (rules && rules.handles) || {};
    const cfg = handles[cslug] || handles['default'];
    const re = cfg ? toRegex(cfg.pattern) : null;
    if (re && !re.test(token.slice(1))) {
        const name = cslug ? cslug.charAt(0).toUpperCase() + cslug.slice(1) : '';
        return { valid: false, message: 'That is not a valid ' + name + ' handle.' };
    }

    return { valid: true, message: '' };
}

/** Validate one or more #hashtags. Mirrors the hashtag branch (allowMultiple=true). */
export function validateHashtags(value, rules) {
    const v = value == null ? '' : String(value).trim();
    if (v === '') {
        return { valid: true, message: '' };
    }

    const tokens = v.split(SPLIT).filter(Boolean);
    if (!tokens.length) {
        return { valid: false, message: 'Please enter valid hashtags separated by spaces (e.g. #trending #music).' };
    }

    const cfg = (rules && rules.hashtag) || null;
    const re = cfg ? toRegex(cfg.pattern) : null;
    for (const token of tokens) {
        if (!/^#[^\s@#]+$/u.test(token)) {
            return { valid: false, message: 'Each hashtag must start with # and contain no spaces or @ (e.g. #trending #music).' };
        }
        if (re && !re.test(token.slice(1))) {
            return { valid: false, message: 'Hashtags may only contain letters, numbers and underscores — no spaces or symbols inside a tag.' };
        }
    }

    return { valid: true, message: '' };
}

export function validateMediaUrl(value, slug, rules) {
    const v = value == null ? '' : String(value).trim();
    if (v === '') {
        return { valid: true, message: '' };
    }

    let host;
    try {
        const parsed = new URL(v);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            return { valid: false, message: 'Please enter a valid URL.' };
        }
        host = parsed.hostname.toLowerCase().replace(/^(www\.|m\.)/, '');
    } catch (e) {
        return { valid: false, message: 'Please enter a valid URL.' };
    }

    const cslug = canonicalSlug(slug, rules);
    const domains = (rules && rules.media_domains) || {};
    const allowed = domains[cslug] || null;
    if (allowed === null) {
        return { valid: true, message: '' };
    }

    if (allowed.indexOf(host) === -1) {
        const name = cslug ? cslug.charAt(0).toUpperCase() + cslug.slice(1) : '';
        return { valid: false, message: 'The URL must be a valid ' + name + ' link.' };
    }

    return { valid: true, message: '' };
}

const api = { normalizeMention, normalizeHashtag, validateMention, validateHashtags, validateMediaUrl };

if (typeof window !== 'undefined') {
    window.SocialTokens = api;
}

export default api;
